import { ref } from 'vue';

/**
 * Composable que encapsula a álgebra de condition groups da Automação.
 *
 * Portado 1:1 de AddAutomationRule.vue / EditAutomationRule.vue. NÃO
 * alterar a lógica de operadores — o round-trip flat↔grupos
 * (convertConditionsToGroups / convertGroupsToConditions) precisa ficar
 * idêntico ao comportamento legado pra não quebrar automações salvas.
 *
 * O estado (conditionGroups, globalGroupOperator, groupCounter,
 * copyPreviousGroup) é interno ao composable e reativo.
 */
export function useAutomationConditionGroups() {
  const conditionGroups = ref([]);
  const globalGroupOperator = ref('and');
  const copyPreviousGroup = ref(false);
  // groupCounter NÃO é ref: é só um contador incremental pra ids de
  // grupo (key do v-for). Não precisa de reatividade.
  let groupCounter = 0;
  // Retorna o id atual e incrementa (substitui groupCounter++ pra
  // satisfazer no-plusplus).
  const nextGroupId = () => {
    const id = groupCounter;
    groupCounter += 1;
    return id;
  };

  // Criar condição padrão
  const createDefaultCondition = () => {
    return {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    };
  };

  // Criar grupo padrão
  const createDefaultGroup = () => {
    return {
      id: nextGroupId(),
      internalOperator: 'and',
      conditions: [createDefaultCondition()],
      groupOperator: globalGroupOperator.value,
    };
  };

  // Clonar um grupo
  const cloneGroup = group => {
    return {
      id: nextGroupId(),
      internalOperator: group.internalOperator,
      conditions: group.conditions.map(condition => ({
        ...condition,
        values: Array.isArray(condition.values)
          ? [...condition.values]
          : condition.values,
      })),
      groupOperator: globalGroupOperator.value,
    };
  };

  // Converter condições flat para grupos
  const convertConditionsToGroups = flatConditions => {
    if (!flatConditions || flatConditions.length === 0) {
      return [createDefaultGroup()];
    }

    const hasGroupIndex = flatConditions.some(c => c.group_index !== undefined);

    if (hasGroupIndex) {
      const groupsMap = {};

      flatConditions.forEach(condition => {
        const idx = condition.group_index ?? 0;
        if (!groupsMap[idx]) {
          groupsMap[idx] = {
            id: nextGroupId(),
            internalOperator: condition.query_operator || 'and',
            conditions: [],
            groupOperator: null,
          };
        }
        groupsMap[idx].conditions.push({
          attribute_key: condition.attribute_key,
          filter_operator: condition.filter_operator,
          values: Array.isArray(condition.values)
            ? [...condition.values]
            : condition.values,
          query_operator: condition.query_operator || 'and',
          custom_attribute_type: condition.custom_attribute_type || '',
        });
      });

      const groupIndices = Object.keys(groupsMap)
        .map(Number)
        .sort((a, b) => a - b);
      const groups = groupIndices.map((idx, i) => {
        const group = groupsMap[idx];
        if (group.conditions.length > 0) {
          group.internalOperator = group.conditions[0].query_operator || 'and';
        }

        if (i < groupIndices.length - 1) {
          const lastCondition = group.conditions[group.conditions.length - 1];
          group.groupOperator = lastCondition.query_operator || 'or';
        }
        return group;
      });

      if (groups.length > 0) {
        const lastGroupIndex = groups.length - 1;
        const previousGroupOperator =
          lastGroupIndex > 0 ? groups[lastGroupIndex - 1].groupOperator : null;
        groups[lastGroupIndex].groupOperator =
          previousGroupOperator || globalGroupOperator.value || 'and';
      }

      if (groups.length > 1 && groups[0].groupOperator) {
        globalGroupOperator.value = groups[0].groupOperator;
      }

      return groups.length > 0 ? groups : [createDefaultGroup()];
    }

    // Sem group_index - criar um único grupo
    const internalOperator = flatConditions[0]?.query_operator || 'and';
    return [
      {
        id: nextGroupId(),
        internalOperator,
        conditions: flatConditions.map(condition => ({
          attribute_key: condition.attribute_key,
          filter_operator: condition.filter_operator,
          values: Array.isArray(condition.values)
            ? [...condition.values]
            : condition.values,
          query_operator: condition.query_operator || 'and',
          custom_attribute_type: condition.custom_attribute_type || '',
        })),
        groupOperator: null,
      },
    ];
  };

  // Converter grupos para condições flat
  const convertGroupsToConditions = groups => {
    const flatConditions = [];

    groups.forEach((group, groupIndex) => {
      group.conditions.forEach((condition, conditionIndex) => {
        if (!condition.attribute_key) return;

        let queryOperator = null;
        const isLastConditionInGroup =
          conditionIndex === group.conditions.length - 1;
        const isLastGroup = groupIndex === groups.length - 1;

        if (!isLastConditionInGroup) {
          queryOperator = group.internalOperator || 'and';
        } else if (!isLastGroup) {
          queryOperator = globalGroupOperator.value || 'or';
        }

        flatConditions.push({
          attribute_key: condition.attribute_key,
          filter_operator: condition.filter_operator,
          values: condition.values,
          query_operator: queryOperator,
          custom_attribute_type: condition.custom_attribute_type || '',
          group_index: groupIndex,
        });
      });
    });

    return flatConditions;
  };

  // Inicializar grupos a partir de condições flat
  const initGroups = flatConditions => {
    conditionGroups.value = convertConditionsToGroups(flatConditions);
  };

  // Adicionar novo grupo
  const addGroup = () => {
    if (conditionGroups.value.length > 0) {
      conditionGroups.value[conditionGroups.value.length - 1].groupOperator =
        globalGroupOperator.value;
    }

    if (copyPreviousGroup.value && conditionGroups.value.length > 0) {
      const lastGroup = conditionGroups.value[conditionGroups.value.length - 1];
      const clonedGroup = cloneGroup(lastGroup);
      conditionGroups.value.push(clonedGroup);
    } else {
      const newGroup = createDefaultGroup();
      conditionGroups.value.push(newGroup);
    }
  };

  // Remover grupo
  const removeGroup = groupIndex => {
    if (conditionGroups.value.length === 1) {
      conditionGroups.value = [createDefaultGroup()];
    } else {
      conditionGroups.value.splice(groupIndex, 1);
      conditionGroups.value.forEach(group => {
        group.groupOperator = globalGroupOperator.value;
      });
    }
  };

  // Atualizar operador global entre grupos
  const updateGlobalGroupOperator = newOperator => {
    globalGroupOperator.value = newOperator;
    conditionGroups.value.forEach(group => {
      group.groupOperator = newOperator;
    });
  };

  // Atualizar um grupo
  const updateGroup = (groupIndex, newGroup) => {
    conditionGroups.value[groupIndex] = newGroup;
  };

  // Reset filter dentro de um grupo
  const handleResetFilter = (
    groupIndex,
    conditionIndex,
    currentCondition,
    automationTypes,
    eventName
  ) => {
    const group = conditionGroups.value[groupIndex];
    const newConditions = [...group.conditions];

    const conditionType = automationTypes[eventName]?.conditions?.find(
      condition => condition.key === currentCondition.attribute_key
    );

    newConditions[conditionIndex] = {
      ...newConditions[conditionIndex],
      filter_operator: conditionType?.filterOperators?.[0]?.value || 'equal_to',
      values: '',
    };

    conditionGroups.value[groupIndex] = {
      ...group,
      conditions: newConditions,
    };
  };

  // Converter chaves de erro (condition_0, condition_1) para formato com
  // grupo (condition_0_0, condition_0_1)
  const convertErrorKeysToGroupFormat = errors => {
    const convertedErrors = {};
    let flatIndex = 0;

    conditionGroups.value.forEach((group, groupIndex) => {
      group.conditions.forEach((_, conditionIndex) => {
        const flatKey = `condition_${flatIndex}`;
        const groupKey = `condition_${groupIndex}_${conditionIndex}`;

        if (errors[flatKey]) {
          convertedErrors[groupKey] = errors[flatKey];
        }
        flatIndex += 1;
      });
    });

    // Copiar erros de actions e outros campos
    Object.keys(errors).forEach(key => {
      if (!key.startsWith('condition_')) {
        convertedErrors[key] = errors[key];
      }
    });

    return convertedErrors;
  };

  return {
    conditionGroups,
    globalGroupOperator,
    copyPreviousGroup,
    convertConditionsToGroups,
    convertGroupsToConditions,
    initGroups,
    addGroup,
    removeGroup,
    updateGlobalGroupOperator,
    updateGroup,
    handleResetFilter,
    convertErrorKeysToGroupFormat,
  };
}
